import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';

export interface WorldCupEventTeam {
  name: string;
  logo: string | null;
  abbr: string | null;
}

export interface WorldCupEventScore {
  home: number;
  away: number;
  status: string;
  status_detail: string;
  winner_home: boolean;
  winner_away: boolean;
  score_home_by_period: number[];
  score_away_by_period: number[];
  display_clock: string | null;
  game_period: number;
  score_updated_at: string | null;
}

export interface WorldCupEventDetail {
  slug: string;
  polymarket_id: number;
  title: string;
  group: string | null;
  source: string;
  game_start_time: string | null;
  end_date: string | null;
  home_team: WorldCupEventTeam;
  away_team: WorldCupEventTeam;
  score: WorldCupEventScore | null;
}

export interface WorldCupEventResponse {
  success: boolean;
  data: WorldCupEventDetail;
  message?: string;
}

export interface UseWorldCupEventOptions {
  eventSlug?: string | null;
  enabled?: boolean;
  swr?: SWRConfiguration<WorldCupEventResponse, Error>;
}

export async function fetchWorldCupEvent(eventSlug: string) {
  const searchParams = new URLSearchParams({
    event_slug: eventSlug,
  });

  const response = await customFetch(`/api/world-cup/game?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch World Cup event');
  }

  const result = (await response.json()) as WorldCupEventResponse;

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch World Cup event');
  }

  return result;
}

export function isLiveStatus(status?: string) {
  return (
    status &&
    ['IN_PROGRESS', 'STATUS_IN_PROGRESS', 'STATUS_SECOND_HALF', 'STATUS_FIRST_HALF'].includes(
      status,
    )
  );
}

export function useWorldCupEvent(options: UseWorldCupEventOptions = {}) {
  const eventSlug = options.eventSlug?.trim();
  const enabled = options.enabled ?? true;
  const swrKey = enabled && eventSlug ? ['world-cup-event', eventSlug] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<WorldCupEventResponse, Error>(
    swrKey,
    () => fetchWorldCupEvent(eventSlug!),
    {
      revalidateOnFocus: false,
      refreshInterval: (res) => {
        if (isLiveStatus(res?.data?.score?.status)) {
          return 1 * 60 * 1000;
        }
        return 0;
      },

      ...options.swr,
    },
  );

  return {
    data: data?.data ?? null,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useWorldCupEvent;
